// src/utils/generateLeadMagnet.js
// Free 1-page teaser PDF (Key Dates + Patron Saints) used as an email-gated
// lead magnet. Deliberately smaller than the premium cheat sheet — a taste
// of the content quality, not the full product. Facts mirror the live
// KeyDatesTimeline.js and PatronSaintsAndSymbols.js pages exactly.
import { cleanPDF } from './generateCheatSheet';
import { TOP_KEY_DATES, PATRON_SAINTS, SAMPLE_QUESTIONS } from './leadMagnetData';

export const generateLeadMagnet = (doc, autoTable) => {
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 14;

    doc.setFontSize(22);
    doc.setTextColor(63, 81, 181);
    doc.text(cleanPDF('Life in the UK: Free Cheat Sheet'), pageW / 2, 18, { align: 'center' });
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text(cleanPDF('Key Dates & Patron Saints, handbook-verified'), pageW / 2, 25, { align: 'center' });

    let y = 34;

    doc.setFontSize(14);
    doc.setTextColor(63, 81, 181);
    doc.text(cleanPDF('Key Dates'), margin, y);
    y += 4;

    autoTable(doc, {
        startY: y,
        head: [['Year', 'Event', 'What it means']],
        body: TOP_KEY_DATES.map(row => row.map(cleanPDF)),
        theme: 'grid',
        styles: { fontSize: 7.5, cellPadding: 1.5 },
        headStyles: { fillColor: [63, 81, 181] },
        margin: { left: margin, right: margin },
    });

    y = doc.lastAutoTable.finalY + 8;

    doc.setFontSize(14);
    doc.setTextColor(63, 81, 181);
    doc.text(cleanPDF('Patron Saints of the UK'), margin, y);
    y += 4;

    autoTable(doc, {
        startY: y,
        head: [['Nation', 'Saint', 'Day', 'Status']],
        body: PATRON_SAINTS.map(row => row.map(cleanPDF)),
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [63, 81, 181] },
        margin: { left: margin, right: margin },
    });

    y = doc.lastAutoTable.finalY + 8;

    /* Sample questions.
       The tables above are a reference sheet, and a reference sheet lets someone
       feel prepared without ever discovering what the test actually asks. These
       four are real questions from the exam bank, drawn from topics the tables
       do NOT cover — councils, driving ages, sport, modern politics. Reading
       them is how someone learns that 12 dates and 4 saints is not enough.
       Showing the gap beats claiming it. */
    doc.setFontSize(14);
    doc.setTextColor(63, 81, 181);
    doc.text(cleanPDF('Try four real questions'), margin, y);
    y += 5;

    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(cleanPDF('From the same bank as the mock exams. Answers at the bottom.'), margin, y);
    y += 5;

    doc.setFontSize(8.5);
    SAMPLE_QUESTIONS.forEach((item, i) => {
        doc.setTextColor(40, 40, 40);
        // splitTextToSize wraps rather than overflowing the page width
        const qLines = doc.splitTextToSize(cleanPDF(`${i + 1}. ${item.q}`), pageW - margin * 2);
        doc.text(qLines, margin, y);
        y += qLines.length * 4;

        doc.setTextColor(90, 90, 90);
        const opts = item.choices.map((c, n) => `${String.fromCharCode(65 + n)}) ${c}`).join('   ');
        const oLines = doc.splitTextToSize(cleanPDF(opts), pageW - margin * 2 - 4);
        doc.text(oLines, margin + 4, y);
        y += oLines.length * 4 + 2;
    });

    // Answers on one line, small — findable but not readable at a glance while
    // the reader is still attempting the questions above.
    doc.setFontSize(7.5);
    doc.setTextColor(130, 130, 130);
    const answers = SAMPLE_QUESTIONS
        .map((item, i) => `${i + 1}${String.fromCharCode(65 + item.correct)}`)
        .join('   ');
    doc.text(cleanPDF(`Answers: ${answers}`), margin, y);
    y += 8;

    /* Premium panel.
       Names what is MISSING, not what is included. "1,000+ questions and 45 mock
       exams" is a feature list; "this sheet covers 2 of 12 topics" tells the
       reader they are holding a fraction, which is both true and the reason to
       buy. */
    doc.setFillColor(238, 242, 255);
    doc.roundedRect(margin, y, pageW - margin * 2, 24, 3, 3, 'F');
    doc.setFontSize(10);
    doc.setTextColor(63, 81, 181);
    doc.text(cleanPDF('This sheet covers 2 of 12 topics.'), margin + 5, y + 7.5);
    doc.setFontSize(8.5);
    doc.setTextColor(80, 80, 80);
    doc.text(cleanPDF('The other 10 (government, law, culture, modern Britain) are in the full'), margin + 5, y + 13);
    doc.text(cleanPDF('guide, with 1,080 practice questions and all 45 mock exams.'), margin + 5, y + 17.5);
    doc.setTextColor(63, 81, 181);
    doc.text(cleanPDF('Unlock Premium (GBP 7.99, one-off) at lifeinukcoach.co.uk/pricing'), margin + 5, y + 22);
};
